import React, { useState, useEffect, useRef } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { ActiveInput, ActiveLabel, DisabledInput, DisabledLabel, ErrorInput, ErrorLabel } from './Util.jsx';
import InfoBox from './InfoBox.jsx';

const DatePicker = ({
    label,
    value,
    setValue,
    error = false,
    disabled = false,
    readOnly = false,
    classes = '',
    info = false,
    infotext = '',
    infopos = 'r',
    infoclass = '',
    infosize = 20
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const inputRef = useRef(null);
    const calendarRef = useRef(null);

    useEffect(() => {
        if (value !== '' && value !== null) {
            let year, month, day;
            if (value.includes('/')) {
                // formato DD/MM/YYYY
                [day, month, year] = value.split('/').map(Number);
            } else {
                // formato YYYY-MM-DD
                [year, month, day] = value.split('-').map(Number);
            }
            const date = new Date(year, month - 1, day);
            setSelectedOption({
                month: date.getMonth(),
                year: date.getFullYear()
            });
        }
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target) &&
                inputRef.current && !inputRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleInputClick = () => {
        if (!disabled && !readOnly) {
            setIsOpen(true);
        }
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setValue('');
    };

    const handleDaySelect = (day, month, year) => {
        setValue(`${year}-${month + 1 < 10 ? '0' + (month + 1) : month + 1}-${day < 10 ? '0' + day : day}`);
        setIsOpen(false);
    };

    const handleMonthChange = (increment) => {
        setSelectedOption((prev) => {
            const currentMonth = prev?.month ?? new Date().getMonth();
            const currentYear = prev?.year ?? new Date().getFullYear();

            let newMonth, newYear;

            if (typeof increment === 'number') {
                // Direct month selection
                newMonth = increment;
                newYear = currentYear;
            } else {
                // Increment/decrement
                newMonth = increment ?
                    (currentMonth + 1 > 11 ? 0 : currentMonth + 1) :
                    (currentMonth - 1 < 0 ? 11 : currentMonth - 1);
                newYear = increment ?
                    (currentMonth + 1 > 11 ? currentYear + 1 : currentYear) :
                    (currentMonth - 1 < 0 ? currentYear - 1 : currentYear);
            }

            return {
                ...prev,
                month: newMonth,
                year: newYear
            };
        });
    };

    const handleYearChange = (year) => {
        setSelectedOption((prev) => ({
            ...prev,
            year: parseInt(year, 10),
        }));
    };

    const renderCalendarDays = () => {
        const currentDate = new Date();
        const year = selectedOption?.year || currentDate.getFullYear();
        const month = selectedOption?.month !== undefined ? selectedOption.month : currentDate.getMonth();
        const firstDayOfMonth = new Date(year, month, 1);
        const startDay = (firstDayOfMonth.getDay() + 6) % 7; // Adjust to start week on Monday
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const [selectedYear, selectedMonth, selectedDay] = value?.split('-').map(Number) || [];

        return Array.from({ length: 42 }, (_, index) => {
            const day = index - startDay + 1;
            const isCurrentDay = day === currentDate.getDate() &&
                month === currentDate.getMonth() &&
                year === currentDate.getFullYear();
            const isSelectedDay = day === selectedDay &&
                month === selectedMonth - 1 &&
                year === selectedYear;
            const isValidDay = day > 0 && day <= daysInMonth;

            return (
                <button
                    key={index}
                    onClick={(e) => {
                        e.preventDefault();
                        if (isValidDay) handleDaySelect(day, month, year);
                    }}
                    className={`p-1 rounded ${isValidDay
                        ? `cursor-pointer ${isSelectedDay
                            ? 'bg-blue-300 dark:bg-blue-800 hover:bg-blue-400 hover:dark:bg-blue-700'
                            : 'hover:bg-gray-200 hover:dark:bg-neutral-800'
                        } ${isCurrentDay && !isSelectedDay ? 'text-blue-600 underline font-semibold' : ''}`
                        : 'text-gray-300 pointer-events-none'
                        }`}
                >
                    {isValidDay ? day : ''}
                </button>
            );
        });
    };

    return (
        <div className={`${classes}`} onMouseDown={(e) => e.preventDefault()}>
            <div className="relative w-full">
                <input
                    type="text"
                    ref={inputRef}
                    className={disabled ? DisabledInput : error ? ErrorInput : ActiveInput}
                    placeholder=" "
                    value={value}
                    readOnly
                    onClick={handleInputClick}
                    onFocus={handleInputClick}
                    onChange={() => { }} // Empty to satisfy React
                />
                <label
                    className={`hover:cursor-text rounded-lg ${disabled ? DisabledLabel : error ? ErrorLabel : ActiveLabel}`}
                    onClick={() => !disabled && inputRef.current.focus()}
                >
                    {label}
                </label>

                {value && !disabled && !readOnly && (
                    <div
                        className="absolute inset-y-0 right-6 flex items-center text-gray-700 dark:text-neutral-300 hover:cursor-pointer"
                        onClick={handleClear}
                    >
                        <X size={20} />
                    </div>
                )}

                <div className="absolute inset-y-0 right-0 flex items-center pointer-events-none text-gray-700 dark:text-neutral-300" onClick={handleInputClick}>
                    <CalendarDays size={20} />
                </div>

                {info && (
                    <div className="absolute inset-y-0 -right-6 flex items-center hover:cursor-pointer">
                        <InfoBox text={infotext} position={infopos} size={infosize} classes={infoclass} />
                    </div>
                )}
            </div>
            {isOpen && (
                <div
                    ref={calendarRef}
                    className="mt-1 bg-gray-100 dark:bg-neutral-900 border-2 border-gray-300 dark:border-neutral-700 rounded-lg w-full px-2 pt-2"
                >
                    <div className="flex justify-between items-center mb-2">
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                handleMonthChange(false);
                            }}
                            className="text-gray-700 dark:text-neutral-300 hover:cursor-pointer"
                        >
                            <ChevronLeft size={20} />
                        </button>

                        <div className="flex gap-2">
                            <span
                                className="font-semibold hover:cursor-pointer hover:bg-gray-200 hover:dark:bg-neutral-800 px-3 py-1 rounded"
                                onClick={(e) => {
                                    e.preventDefault();
                                    const month = prompt("Inserisci il mese (1-12):");
                                    if (month >= 1 && month <= 12) {
                                        handleMonthChange(month - 1);
                                    }
                                }}
                            >
                                {selectedOption?.month !== undefined
                                    ? new Date(0, selectedOption.month).toLocaleString('default', { month: 'long' })
                                    : new Date().toLocaleString('default', { month: 'long' })}
                            </span>
                            <span
                                className="font-semibold hover:cursor-pointer hover:bg-gray-200 hover:dark:bg-neutral-800 px-3 py-1 rounded"
                                onClick={(e) => {
                                    e.preventDefault();
                                    const year = prompt("Inserisci l'anno:");
                                    if (year) {
                                        handleYearChange(year);
                                    }
                                }}
                            >
                                {selectedOption?.year || new Date().getFullYear()}
                            </span>
                        </div>

                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                handleMonthChange(true);
                            }}
                            className="text-gray-700 dark:text-neutral-300 hover:cursor-pointer"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>

                    <div className="grid grid-cols-7 text-center text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">
                        {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((day) => (
                            <div key={day} className="p-1">
                                {day}
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-7 text-center text-sm pb-2">
                        {renderCalendarDays()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DatePicker;